// diagram.store.ts
import { createStore, StoreApi } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import {
  createRfSlice,
  createBrokerSlice,
  createHandlerSlice,
  createResizeSlice,
  createSessionSlice,
  SessionSlice,
  RfSlice,
  BrokerSlice,
  HandlerSlice,
  ResizeSlice,
} from './slices';
import { DiagramModel } from '@diagram/model/diagram.model';

// (UiSlice, PresenceSlice later)

export type DiagramStateProps = {
  nodesById?: RfSlice['nodesById'];
  rfNodes: RfSlice['rfNodes'];
  rfEdges: RfSlice['rfEdges'];
  edgesById?: RfSlice['edgesById'];
  drilldowns?: RfSlice['drilldowns'];
  data?: DiagramModel;
};

let next_ID = 0;

export type DiagramStore = {
  nextId: number;
  permissions: null | 'read-only' | 'read-write';
  data: DiagramModel;
  setData: (data: DiagramModel) => void;
  getState: () => DiagramState;
  subscribe: (
    listener: (state: DiagramState, prevState: DiagramState) => void,
  ) => () => void;
  getInitialState: () => DiagramState;
};

export type DiagramState = RfSlice &
  BrokerSlice &
  HandlerSlice &
  ResizeSlice &
  SessionSlice &
  DiagramStore;

const middleware = <T extends DiagramState>(
  initializer: (
    set: StoreApi<T>['setState'],
    get: StoreApi<T>['getState'],
  ) => T,
) =>
  subscribeWithSelector(
    devtools(
      persist(initializer, {
        name: 'diagram-store',
        version: 1,
        partialize: () => ({
          // Specify which parts of the state to persist
        }),
      }),
    ),
  );

export const createDiagramStore = (initProps?: Partial<DiagramState>) => {
  return createStore<DiagramState>()(
    middleware<DiagramState>((set, get) => ({
      nextId: next_ID++,
      data: {
        id: '',
        name: '',
        organizationId: '',
        settings: {
          snapToGrid: false,
          autoSpacing: false,
          gridSize: 10,
          theme: 'light',
          nodeDistance: 150,
        },
        users: {},
        access: 'private',
        description: '',
      },
      permissions: null,
      setData: (data: DiagramModel) => set({ data }),
      getInitialState: () => get(),
      getState: () => get(),
      subscribe: (listener) => get().subscribe(listener),
      ...createRfSlice(set, get),
      ...createBrokerSlice(set),
      ...createHandlerSlice(set, get),
      ...createResizeSlice(set, get),
      ...createSessionSlice(set, get),
      ...initProps,
    })),
  );
};

let _diagramStore: StoreApi<DiagramState> | null = null;

export const setDiagramStore = (store: StoreApi<DiagramState>) => {
  _diagramStore = store;
};

export const clearDiagramStore = () => {
  _diagramStore = null;
};

export const getDiagramStore = (): StoreApi<DiagramState> => {
  if (!_diagramStore) {
    _diagramStore = createDiagramStore();
  }
  return _diagramStore;
};

// if (typeof window !== 'undefined') {
//   if (!_diagramStore) {
//     startFlusher(getDiagramStore());
//   }
// }
